import { Button, Dropdown, MenuProps } from 'antd';
import React from 'react';
import { Link, Icon } from '@common/components';

import { DisputeListItemFragmentFragment } from '@common/graphql';
import { buildDialogUrl, createDropdownMenuItem } from '@common/services';
import { PerformDisputeActionButton } from '@pay-bill/components';

type DisputeActionsDispute = Pick<
  DisputeListItemFragmentFragment,
  | 'code'
  | 'id'
  | 'canRetract'
  | 'canReopen'
  | 'canReject'
  | 'canEdit'
  | 'canRedirectToOrganization'
  | 'canRedirectToSupplier'
  | 'canRetractRedirect'
  | 'canAccept'
  | 'canApprove'
>;

const generateDisputeMenuItems = (
  dispute: DisputeActionsDispute
): MenuProps['items'] => {
  const { code, id } = dispute;

  const buildDisputeViewDrawerUrl = (action: string) =>
    buildDialogUrl('dispute-view-drawer', {
      props: { action, disputeCode: code },
      overlap: false,
    });

  return [
    {
      key: '2',
      style: { width: '180px' },
      label: <Link to={buildDisputeViewDrawerUrl('review')}>View Dispute</Link>,
    },
    ...(createDropdownMenuItem({
      key: '3',
      label: (
        <PerformDisputeActionButton
          state="retract"
          text="Retract Dispute"
          buttonType="text"
          disputeId={id}
        />
      ),
      condition: dispute.canRetract.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '4',
      label: (
        <Link to={buildDisputeViewDrawerUrl('reopen')}>Reopen Dispute</Link>
      ),
      condition: dispute.canReopen.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '5',
      label: (
        <Link to={buildDisputeViewDrawerUrl('reject')}>Reject Dispute</Link>
      ),
      condition: dispute.canReject.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '6',
      label: <Link to={buildDisputeViewDrawerUrl('edit')}>Edit Dispute</Link>,
      condition: dispute.canEdit.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '7',
      label: (
        <Link to={buildDisputeViewDrawerUrl('redirect')}>
          Send Dispute to Organization
        </Link>
      ),
      condition: dispute.canRedirectToOrganization.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '8',
      label: (
        <Link to={buildDisputeViewDrawerUrl('redirect')}>
          Send Dispute to Supplier
        </Link>
      ),
      condition: dispute.canRedirectToSupplier.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '9',
      label: (
        <PerformDisputeActionButton
          state="retract_redirect"
          text="Retract Dispute Forwarding"
          buttonType="text"
          disputeId={id}
        />
      ),
      condition: dispute.canRetractRedirect.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '10',
      label: (
        <Link to={buildDisputeViewDrawerUrl('accept')}>Accept Dispute</Link>
      ),
      condition: dispute.canAccept.value,
    }) ?? []),
    ...(createDropdownMenuItem({
      key: '11',
      label: (
        <Link to={buildDisputeViewDrawerUrl('approve')}>Approve Dispute</Link>
      ),
      condition: dispute.canApprove.value,
    }) ?? []),
  ];
};

const generateInvoiceItemsMenu = (item: {
  id: string;
  canSubmitDispute: { value: boolean };
  resolvedDispute?: DisputeActionsDispute;
  dispute?: DisputeActionsDispute;
}): MenuProps['items'] => {
  const dispute = item.resolvedDispute || item.dispute;

  const addDisputeDrawerUrl = buildDialogUrl('dispute-create-drawer', {
    props: { invoiceItemId: item.id },
    overlap: true,
  });

  return [
    ...(createDropdownMenuItem({
      key: '1',
      label: <Link to={addDisputeDrawerUrl}>Dispute</Link>,
      condition: item.canSubmitDispute.value,
    }) ?? []),
    ...((dispute ? generateDisputeMenuItems(dispute) : []) ?? []),
  ];
};

export const DisputeActionsDropdown = ({
  listType,
  dispute,
  item,
}: {
  listType: 'invoiceItems' | 'disputes';
  dispute?: DisputeActionsDispute;
  item?: {
    id: string;
    canSubmitDispute: { value: boolean };
    resolvedDispute?: DisputeActionsDispute;
    dispute?: DisputeActionsDispute;
  };
}) => {
  const menuItems =
    listType === 'disputes' && dispute
      ? generateDisputeMenuItems(dispute)
      : listType === 'invoiceItems' && item
        ? generateInvoiceItemsMenu(item)
        : [];

  return (
    <Dropdown
      className="dispute-actions-dropdown"
      menu={{ items: menuItems }}
      trigger={['click']}
    >
      <a>
        <Button
          type="text"
          shape="circle"
          color="elm"
          style={{ padding: 5 }}
          icon={
            <Icon
              size="lg"
              icon="verticalEllipsis"
              kind="regular"
              color="elm"
            />
          }
        />
      </a>
    </Dropdown>
  );
};
